import { SearchResult } from '@/features/header/components/TopBar/SearchDropdown';

export const MIN_SEARCH_LENGTH = 3;
export const MAX_RESULTS = 50;

const normalize = (value: string) => value.toLowerCase().trim();

export const isValidSearchTerm = (term: string): boolean =>
  normalize(term).length >= MIN_SEARCH_LENGTH;

const shouldExcludeItem = (item: SearchResult, isProfileSearch: boolean, currentSearch: string): boolean => {
  const isGroupOrTheme = item.type === 'group' || item.type === 'theme';

  if (isProfileSearch) {
    if (currentSearch.includes(',')) {
      return true;
    }

    return isGroupOrTheme;
  }

  return !isGroupOrTheme;
};

const itemMatchesWords = (item: SearchResult, words: string[]): boolean => {
  const itemNameLower = normalize(item.name);
  return words.every((word) => itemNameLower.includes(word));
};

const normalizeStringItem = (item: string, index: number): SearchResult => {
  const itemLower = item.toLowerCase();
  const isGroup = itemLower.startsWith('grupo:');
  const isTheme = itemLower.startsWith('tema:');
  const name = item.replace(/^(grupo:|tema:)\s*/i, '').trim();

  let type = 'profile';
  if (isGroup) {
    type = 'group';
  } else if (isTheme) {
    type = 'theme';
  }

  return {
    id: `auto-${index}`,
    name,
    type,
    label: item
  };
};

const normalizeObjectItem = (item: any, index: number): SearchResult => {
  if (item.type === 'group' || item.type === 'theme') {
    const typeLabel = item.type === 'group' ? 'Grupo: ' : 'Tema: ';
    return {
      ...item,
      label: `${typeLabel}${item.name}`
    };
  }
  return {
    id: item.uid ?? item.id ?? `auto-${index}`,
    name: item.name,
    type: item.type ?? 'profile',
    channel: item.channel,
    label: item.channel ? `Perfil ${item.channel}: ${item.name}` : item.label
  };
};

const normalizeItem = (item: any, index: number): SearchResult => {
  if (typeof item === 'string') {
    return normalizeStringItem(item, index);
  }

  if (item && typeof item === 'object' && !item.label) {
    return normalizeObjectItem(item, index);
  }
  return item;
};

export const filterSearchResults = (
  term: string,
  availableItems: any[],
  maxResults: number = MAX_RESULTS
): SearchResult[] => {
  const normalizedTerm = normalize(term);

  if (!isValidSearchTerm(term)) {
    return [];
  }

  const normalizedItems: SearchResult[] = availableItems.map((item, index) => normalizeItem(item, index));

  const isProfileSearch = normalizedTerm.startsWith('@');
  const rawTerm = isProfileSearch ? normalizedTerm.slice(1).trim() : normalizedTerm;
  const words = rawTerm.split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return [];
  }

  const results: SearchResult[] = [];

  for (const item of normalizedItems) {
    if (results.length >= maxResults) {
      break;
    }

    if (!item || shouldExcludeItem(item, isProfileSearch, term)) {
      continue;
    }

    if (itemMatchesWords(item, words)) {
      results.push(item);
    }
  }

  return results;
};